// src/admin/adminApi.js
import { apiFetch } from '../auth/apiFetch.js';

const ADMIN = '/api/v1/admin';

// ── Users ──────────────────────────────────────────────────────────
export async function fetchUsers({ limit = 20, offset = 0, email, tenant } = {}) {
  const params = new URLSearchParams({ limit, offset });
  if (email) params.set('email', email);
  if (tenant) params.set('tenant', tenant);
  const res = await apiFetch(`${ADMIN}/users?${params}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to fetch users: ${res.status}`);
  }
  return res.json();
}

export async function createUser({ email, password }) {
  const res = await apiFetch(`${ADMIN}/users`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to create user: ${res.status}`);
  }
  return res.json();
}

export async function deactivateUser(userId) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/deactivate`, { method: 'POST' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to deactivate: ${res.status}`);
  }
  return res.json();
}

export async function activateUser(userId) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/activate`, { method: 'POST' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to activate: ${res.status}`);
  }
  return res.json();
}

export async function fetchUser(userId) {
  const res = await apiFetch(`${ADMIN}/users/${userId}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to fetch user: ${res.status}`);
  }
  return res.json();
}

export async function updateUserEmail(userId, email) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/email`, {
    method: 'PATCH',
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to update email: ${res.status}`);
  }
  return res.json();
}

export async function resetUserPassword(userId, password) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/password`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to reset password: ${res.status}`);
  }
}

export async function grantSuperadmin(userId) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/grant-superadmin`, { method: 'POST' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to grant superadmin: ${res.status}`);
  }
  return res.json();
}

export async function revokeSuperadmin(userId) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/revoke-superadmin`, { method: 'POST' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to revoke superadmin: ${res.status}`);
  }
  return res.json();
}

export async function fetchUserTenants(userId) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/tenants`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to fetch user tenants: ${res.status}`);
  }
  return res.json();
}

export async function assignUserToTenant(tenantId, email, role = 'editor') {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/users`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to assign user: ${res.status}`);
  }
}

export async function removeUserFromTenant(tenantId, userId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/users/${userId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to remove user: ${res.status}`);
  }
  return res.json();
}

export async function changeUserRole(tenantId, userId, role) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/users/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to change role: ${res.status}`);
  }
}

export async function fetchApplications() {
  const res = await apiFetch(`${ADMIN}/applications`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to fetch apps: ${res.status}`);
  }
  return res.json();
}

// Self-service (any authenticated user, NOT admin-only)
export async function changeOwnPassword(currentPassword, newPassword) {
  const res = await apiFetch('/api/v1/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to change password: ${res.status}`);
  }
}

export async function assignAppToTenant(tenantId, applicationId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/apps`, {
    method: 'POST',
    body: JSON.stringify({ application_id: applicationId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to assign app: ${res.status}`);
  }
}

// ── Admin tenants (read-only) ──────────────────────────────────────
export async function adminListTenants() {
  const res = await apiFetch(`${ADMIN}/tenants`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to list tenants: ${res.status}`);
  }
  return res.json();
}

export async function adminGetTenant(tenantId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to fetch tenant: ${res.status}`);
  }
  return res.json();
}

export async function adminListTenantUsers(tenantId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/users`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to list tenant users: ${res.status}`);
  }
  return res.json();
}

export async function adminListTenantApps(tenantId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/apps`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to list tenant apps: ${res.status}`);
  }
  return res.json();
}

// ── Plans & subscriptions (Credits — superadmin only) ──────────────
// Tolerant error parse: unwrap body.detail when present, else read top-level
// (matches the rest of this file's top-level convention; survives either shape).
async function readAdminError(res) {
  const body = await res.json().catch(() => ({}));
  return body && body.detail && typeof body.detail === 'object' ? body.detail : body;
}

// Normalise list responses: BE may return a bare array or { plans: [...] }.
export async function adminListPlans() {
  const res = await apiFetch(`${ADMIN}/plans`);
  if (!res.ok) {
    const err = await readAdminError(res);
    throw new Error(err.message || `Failed to list plans: ${res.status}`);
  }
  const data = await res.json();
  return Array.isArray(data) ? data : (data.plans || []);
}

export async function adminCreatePlan({ display_name, monthly_credits, active = true }) {
  const res = await apiFetch(`${ADMIN}/plans`, {
    method: 'POST',
    // monthly_credits sent as a string — BE stores it as a lossless Decimal.
    body: JSON.stringify({ display_name, monthly_credits: String(monthly_credits), active }),
  });
  if (!res.ok) {
    const err = await readAdminError(res);
    throw new Error(err.message || `Failed to create plan: ${res.status}`);
  }
  return res.json();
}

export async function adminUpdatePlan(planId, patch) {
  const payload = { ...patch };
  if (payload.monthly_credits != null) payload.monthly_credits = String(payload.monthly_credits);
  const res = await apiFetch(`${ADMIN}/plans/${planId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await readAdminError(res);
    if (err.error_code === 'PLAN_NOT_FOUND') throw new Error('Piano non trovato — aggiorna la lista.');
    throw new Error(err.message || `Failed to update plan: ${res.status}`);
  }
  return res.json();
}

// Current subscription for a tenant. Returns null on 404 (no plan assigned).
export async function adminGetTenantSubscription(tenantId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/subscription`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const err = await readAdminError(res);
    throw new Error(err.message || `Failed to fetch subscription: ${res.status}`);
  }
  return res.json();
}

export async function adminSetTenantSubscription(tenantId, planId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/subscription`, {
    method: 'PUT',
    body: JSON.stringify({ plan_id: planId }),
  });
  if (!res.ok) {
    const err = await readAdminError(res);
    if (err.error_code === 'PLAN_NOT_FOUND') throw new Error('Piano non trovato — aggiorna la lista.');
    if (err.error_code === 'SUBSCRIPTION_CONFLICT') throw new Error('Assegnazione concorrente in conflitto — riprova.');
    throw new Error(err.message || `Failed to set subscription: ${res.status}`);
  }
  return res.json();
}
